import { useState, useEffect } from "react";
import { CheckSquare, Square, ChevronRight, Rocket, Plus } from "lucide-react";
import { api, fmt } from "../../lib/api";
import { toast } from "sonner";

/* ----------------- LAUNCH WIZARD ----------------- */
export function LaunchWizard({ onJump }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState(null);
  const [stats, setStats] = useState(null);
  const load = async () => {
    const c = await api.get("/admin/checklist"); setItems(c.data.items);
    const s = await api.get("/admin/settings/launch"); setState(s.data.value || {});
    const st = await api.get("/admin/stats"); setStats(st.data);
  };
  useEffect(() => { load(); }, []);
  const toggle = async (id, done) => { await api.patch(`/admin/checklist/${id}`, { done: !done }); load(); };
  const launch = async () => {
    if (!window.confirm("Launch NOWCOMMAND now? This starts the 21-day $44 founder window.")) return;
    try { await api.post("/admin/launch"); toast.success("LAUNCHED."); load(); }
    catch (e) { console.error("Launch failed", e); toast.error("Launch failed"); }
  };
  if (!items || !state || !stats) return null;
  const done = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;
  return (
    <div className="space-y-8">
      <div className="panel p-8">
        <div className="overline mb-3">// LAUNCH WIZARD</div>
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <h2 className="font-display text-4xl text-cream">{state.launched ? "Launched" : `You're ${pct}% ready.`}</h2>
            <p className="text-textMuted mt-2">{state.launched ? `Promo window ends ${fmt.date(new Date(new Date(state.launch_date).getTime() + state.promo_days * 86400000).toISOString())}.` : "Complete each step. When all checks are green, press LAUNCH."}</p>
          </div>
          <div className="font-display text-6xl text-gold">{done}/{items.length}</div>
        </div>
        <div className="h-2 bg-borderGold mt-6 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-gold transition-all duration-700" style={{ width: `${pct}%` }}></div>
        </div>
      </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-borderGold">
        {[
          { l: "Active members", v: stats.members_active, sub: `${stats.members_full} sovereign / ${stats.members_foundational} foundational` },
          { l: "Drops published", v: stats.drops_published, sub: `${stats.drops} total` },
          { l: "Articles published", v: stats.articles_published, sub: `${stats.vault_articles} vault / ${stats.articles - stats.articles_published} drafts` },
          { l: "Leads + Waitlist", v: stats.leads + stats.waitlist, sub: `${stats.leads} optins / ${stats.waitlist} waitlist` },
        ].map((s) => (
          <div key={s.l} className="bg-void p-6">
            <div className="overline mb-2">{s.l}</div>
            <div className="font-display text-4xl text-cream">{s.v}</div>
            <div className="text-xs font-mono text-textDim mt-2">{s.sub}</div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="font-display text-2xl text-cream mb-4">Step-by-step</h3>
        <div className="space-y-2">
          {items.map((it, idx) => (
            <div key={it.id} className={`panel p-5 flex items-start gap-4 ${it.done ? "opacity-60" : ""}`}>
              <button data-testid={`wiz-check-${it.id}`} onClick={() => toggle(it.id, it.done)} className="shrink-0 mt-1">
                {it.done ? <CheckSquare className="w-6 h-6 text-gold"/> : <Square className="w-6 h-6 text-textMuted hover:text-gold"/>}
              </button>
              <div className="flex-1">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <div className={`font-display text-xl ${it.done ? "line-through text-textMuted" : "text-cream"}`}>{idx + 1}. {it.title}</div>
                </div>
                {it.description && <div className="text-sm text-textMuted mt-1">{it.description}</div>}
                {it.link && (() => {
                  const m = it.link.match(/tab=(.+)/);
                  const tabId = m ? m[1] : null;
                  return tabId ? (
                    <button data-testid={`wiz-jump-${it.id}`} onClick={() => onJump(tabId)} className="overline text-gold hover:text-goldHi mt-3 flex items-center gap-1">Open this section <ChevronRight className="w-3 h-3"/></button>
                  ) : null;
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {!state.launched && (
        <div className="panel p-8 border-gold">
          <h3 className="font-display text-3xl text-cream">Ready to flip the switch?</h3>
          <p className="text-textMuted mt-2">{done < items.length ? `Recommended: finish all ${items.length} steps first. You have ${items.length - done} left.` : "All checks green. Press launch."}</p>
          <button data-testid="wizard-launch-button" onClick={launch} className="btn-gold mt-6"><Rocket className="w-4 h-4"/> PRESS LAUNCH</button>
        </div>
      )}
    </div>
  );
}

/* ----------------- CHECKLIST ----------------- */
export function Checklist() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState(""); const [desc, setDesc] = useState("");
  const load = () => api.get("/admin/checklist").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const toggle = async (id, done) => { await api.patch(`/admin/checklist/${id}`, { done: !done }); load(); };
  const add = async (e) => { e.preventDefault(); if (!title.trim()) return; await api.post("/admin/checklist", { title, description: desc }); setTitle(""); setDesc(""); load(); };
  const done = items.filter((i) => i.done).length;
  return (
    <div>
      <div className="overline mb-3">// DELIVERABLE CHECKLIST &middot; {done}/{items.length} complete</div>
      <div className="space-y-2 mb-8">
        {items.map((it) => (
          <button key={it.id} data-testid={`check-${it.id}`} onClick={() => toggle(it.id, it.done)} className={`w-full text-left panel p-4 flex gap-4 items-start ${it.done ? "opacity-50" : ""}`}>
            {it.done ? <CheckSquare className="w-5 h-5 text-gold mt-0.5"/> : <Square className="w-5 h-5 text-textMuted mt-0.5"/>}
            <div>
              <div className={`font-display text-xl ${it.done ? "line-through text-textMuted" : "text-cream"}`}>{it.title}</div>
              <div className="text-sm text-textMuted">{it.description}</div>
              {it.link && <div className="overline mt-1">{it.link}</div>}
            </div>
          </button>
        ))}
      </div>
      <form onSubmit={add} className="panel p-5">
        <div className="overline mb-3">// ADD ITEM</div>
        <input data-testid="checklist-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        <input data-testid="checklist-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional description" />
        <button data-testid="checklist-add" type="submit" className="btn-gold mt-4"><Plus className="w-4 h-4"/> Add item</button>
      </form>
    </div>
  );
}

/* ----------------- LAUNCH PANEL ----------------- */
export function LaunchPanel() {
  const [state, setState] = useState(null);
  const load = async () => { const s = await api.get("/admin/settings/launch"); setState(s.data.value || {}); };
  useEffect(() => { load(); }, []);
  const launch = async () => {
    if (!window.confirm("Launch NOWCOMMAND now? Starts the 21-day $44 founder window.")) return;
    try { const r = await api.post("/admin/launch"); toast.success("Launched"); setState({ launched: true, launch_date: r.data.launch_date, promo_days: r.data.promo_days }); } catch (e) { console.error("Launch failed", e); toast.error("Launch failed"); }
  };
  const triggerWB = async () => { await api.post("/admin/trigger-winback"); toast.success("Win-back swept"); };
  const triggerDrops = async () => { await api.post("/admin/trigger-drops"); toast.success("Drops swept"); };
  const regen = async () => { await api.post("/admin/regenerate-pdf"); toast.success("Activation Codes PDF regenerated"); };
  const regenBook = async () => { await api.post("/admin/regenerate-welcome-book"); toast.success("Welcome book PDF regenerated"); };
  if (!state) return null;
  return (
    <div className="space-y-6">
      <div className="panel p-8">
        <div className="overline mb-3">// LAUNCH STATUS</div>
        {state.launched ? (
          <><h2 className="font-display text-3xl text-cream">Launched on {fmt.date(state.launch_date)}</h2><p className="text-textMuted mt-2">$44/mo &amp; $500/yr founder window for {state.promo_days} days. Auto-switches to the $77/mo &amp; $777/yr standard when the countdown completes.</p></>
        ) : (
          <><h2 className="font-display text-3xl text-cream">Not launched yet</h2><button onClick={launch} className="btn-gold mt-6"><Rocket className="w-4 h-4"/> Press LAUNCH</button></>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="panel p-5"><h3 className="font-display text-xl text-cream mb-2">Sweep scheduled drops & articles</h3><button onClick={triggerDrops} className="btn-ghost mt-3 text-xs">Run now</button></div>
        <div className="panel p-5"><h3 className="font-display text-xl text-cream mb-2">Trigger win-back</h3><button onClick={triggerWB} className="btn-ghost mt-3 text-xs">Run now</button></div>
        <div className="panel p-5"><h3 className="font-display text-xl text-cream mb-2">Regenerate Activation Codes PDF</h3><button onClick={regen} className="btn-ghost mt-3 text-xs">Regenerate</button></div>
        <div className="panel p-5"><h3 className="font-display text-xl text-cream mb-2">Regenerate The Welcome Gift PDF</h3><p className="text-textMuted text-xs">"The Mammon Breaker — The Welcome Gift"</p><button onClick={regenBook} className="btn-ghost mt-3 text-xs">Regenerate</button></div>
      </div>
      <DominionManager />
    </div>
  );
}

/* ----------------- DOMINION LIBRARY MANAGER ----------------- */
function DominionManager() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/dominion").then((r) => setD(r.data.dominion));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const save = async (patch) => {
    const next = { ...d, ...patch }; setD(next);
    await api.post("/admin/dominion", patch); toast.success("Dominion library updated");
  };
  const uploadFile = async (e, field, purpose) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 60 * 1024 * 1024) { toast.error("Max 60MB. For large audiobooks, paste a hosted URL instead."); return; }
    setBusy(field);
    try {
      const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
      const resp = await api.post("/upload/file", { data, purpose });
      await save({ [field]: resp.data.url });
      toast.success("Uploaded & unlocked for members");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setBusy(""); }
  };
  return (
    <div className="panel p-6" data-testid="dominion-manager">
      <div className="overline mb-1">// THE GIFT — ASSETS</div>
      <p className="text-textMuted text-sm mb-5">The written Gift ships automatically (generated PDF). The spoken edition stays "Forthcoming" until you upload audio or paste a hosted URL — then it unlocks instantly on every member dashboard.</p>

      <div className="grid md:grid-cols-2 gap-6">
        {/* The Gift — Read */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-xl text-cream">The Gift — Read (PDF)</h4>
            <span className="overline">{d.book_status === "available" ? "AVAILABLE" : "FORTHCOMING"}</span>
          </div>
          <p className="text-xs text-textDim">Ships the generated Gift by default. Upload to replace it with your own file.</p>
          <label className="btn-ghost text-xs cursor-pointer inline-flex"><input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadFile(e, "book_url", "dominion_book")}/>{busy === "book_url" ? "Uploading…" : "Upload PDF"}</label>
          <input value={d.book_url || ""} onChange={(e) => setD({ ...d, book_url: e.target.value })} onBlur={() => save({ book_url: d.book_url })} placeholder="…or paste a hosted PDF URL"/>
          <select value={d.book_status} onChange={(e) => save({ book_status: e.target.value })}>
            <option value="available">Available (members can read)</option>
            <option value="forthcoming">Forthcoming (hidden)</option>
          </select>
        </div>

        {/* The Gift — Listen */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-xl text-cream">The Gift — Listen (audio)</h4>
            <span className="overline">{(d.audiobook_status === "available" && d.audiobook_url) ? "AVAILABLE" : "FORTHCOMING"}</span>
          </div>
          <p className="text-xs text-textDim">For large files, hosting elsewhere and pasting the URL is most reliable.</p>
          <label className="btn-ghost text-xs cursor-pointer inline-flex"><input type="file" accept="audio/*" className="hidden" onChange={(e) => uploadFile(e, "audiobook_url", "dominion_audio")}/>{busy === "audiobook_url" ? "Uploading…" : "Upload audio file"}</label>
          <input value={d.audiobook_url || ""} onChange={(e) => setD({ ...d, audiobook_url: e.target.value })} onBlur={() => save({ audiobook_url: d.audiobook_url })} placeholder="…or paste a hosted audio URL (mp3/m4a)"/>
          <select value={d.audiobook_status} onChange={(e) => save({ audiobook_status: e.target.value })}>
            <option value="available">Available (members can listen)</option>
            <option value="forthcoming">Forthcoming (shows as coming soon)</option>
          </select>
        </div>
      </div>
    </div>
  );
}
